import PageLoading from "@/components/PageLoading";
import SelectRole from "@/components/SelectRole";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useAuth from "@/hooks/auth";
import { subscribe } from "@/services/api/Newsletter";
import { saveUserProfile } from "@/services/api/User";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export default function GoogleOnboarding() {
  const router = useRouter();
  const { user, mutate } = useAuth({ middleware: "auth" });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [checkedNewsletter, setCheckedNewsletter] = useState(true);
  const [isSaving, setSaving] = useState(false);

  // Pre-fill from the Google profile once the user is loaded.
  useEffect(() => {
    if (!user) return;
    setFirstName((prev) => prev || user.first_name || "");
    setLastName((prev) => prev || user.last_name || "");
    setRole((prev) => prev || user.role || "");
  }, [user]);

  const goNext = () => {
    if (router.query.redirect_to) {
      location.href = router.query.redirect_to;
      return;
    }
    location.href = "/";
  };

  const save = async (evt) => {
    evt.preventDefault();

    if (!firstName.trim()) {
      return toast.error("First name is required!");
    }
    if (!role) {
      return toast.error("Please select your role to continue.");
    }

    setSaving(true);
    try {
      await saveUserProfile({
        first_name: firstName,
        last_name: lastName,
        role,
      });

      if (checkedNewsletter && process.env.ENV === "production" && user?.email) {
        try {
          const thisYear = new Date().getFullYear();
          await subscribe({
            email: user.email,
            tags: ["Customer", String(thisYear), "Web"],
            ...(firstName && { first_name: firstName }),
            ...(lastName && { last_name: lastName }),
          });
        } catch (error) {
          console.log(
            "Newsletter subscription failed:",
            error.response?.data?.message
          );
        }
      }

      await mutate();
      goNext();
    } catch (error) {
      toast.error("Couldn't save your details — please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return <PageLoading />;
  }

  return (
    <div className='min-h-screen pb-8 bg-gradient-to-b from-white/10 to-background lg:pb-16'>
      <div className='max-w-md px-4 pt-6 mx-auto'>
        <div className='mt-12 lg:mt-20'>
          <p className='mb-2 text-4xl font-bold leading-tight text-white font-display md:text-5xl'>
            Welcome{user.first_name ? ` ${user.first_name}` : ""}!
          </p>
          <p className='mt-1 text-sm text-white lg:text-base lg:mt-3 text-opacity-80'>
            Just a couple of details to finish setting up your account.
          </p>
          <div className='pt-5 lg:pt-8'>
            <form onSubmit={save} className='grid grid-cols-2 gap-x-4 gap-y-5'>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='first-name'>
                  First Name <span className='text-red-500'>*</span>
                </Label>
                <Input
                  type='text'
                  id='first-name'
                  required
                  value={firstName}
                  onInput={(evt) => setFirstName(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='space-y-1.5 text-left'>
                <Label className='text-xs' htmlFor='last-name'>
                  Last Name
                </Label>
                <Input
                  type='text'
                  id='last-name'
                  value={lastName}
                  onInput={(evt) => setLastName(evt.target.value)}
                  className='bg-white/5 hover:bg-white/10'
                />
              </div>
              <div className='col-span-2 space-y-1.5 text-left'>
                <Label className='text-xs'>
                  Role <span className='text-red-500'>*</span>
                </Label>
                <SelectRole
                  isModal={false}
                  value={role}
                  onChange={(value) => setRole(value)}
                />
              </div>
              <div className='col-span-2'>
                <Label className='flex items-start gap-2 text-left text-white'>
                  <Checkbox
                    checked={checkedNewsletter}
                    onCheckedChange={setCheckedNewsletter}
                    className='bg-white border shadow-sm border-background/10'
                  />
                  <span className='font-medium leading-tight cursor-pointer'>
                    Subscribe to our newsletter
                  </span>
                </Label>
              </div>
              <div className='col-span-2 mt-2'>
                <Button
                  type='submit'
                  size='lg'
                  disabled={isSaving}
                  className='w-full text-base text-white bg-gradient-to-tr from-yellow-1 to-red-1'
                >
                  {isSaving && <Spinner />}
                  {isSaving ? "Saving..." : "Continue"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
